import { useRef, useEffect } from 'react';
import { Box } from '@mui/material';

const BAR_COUNT = 140;
const HEIGHT = 56;

// Simple seeded PRNG — same songId always produces same waveform shape
function seededRandom(seed) {
  let s = (parseInt(seed, 10) || 1) * 1664525 + 1013904223;
  return () => {
    s = (Math.imul(s, 1664525) + 1013904223) & 0xffffffff;
    return (s >>> 0) / 0xffffffff;
  };
}

// Smooth the random heights so adjacent bars are correlated (more waveform-like)
function generateWaveform(seed, count) {
  const rand = seededRandom(seed);
  const raw = Array.from({ length: count }, () => rand());
  const sectionCount = 9;
  const sectionLevels = Array.from({ length: sectionCount }, () => 0.45 + rand() * 0.55);

  // Smooth section levels so neighboring sections feel musically connected.
  const smoothedSections = sectionLevels.map((v, i) => {
    const prev = sectionLevels[i - 1] ?? v;
    const next = sectionLevels[i + 1] ?? v;
    return prev * 0.2 + v * 0.6 + next * 0.2;
  });

  // Running average smoothing
  return raw.map((v, i, arr) => {
    const prev = raw[i - 1] ?? v;
    const next = raw[i + 1] ?? v;
    const base = 0.1 + (prev * 0.24 + v * 0.52 + next * 0.24) * 0.9;

    // Interpolate section intensity across the waveform so bars form visible groups.
    const t = i / Math.max(arr.length - 1, 1);
    const sectionPos = t * (sectionCount - 1);
    const left = Math.floor(sectionPos);
    const right = Math.min(left + 1, sectionCount - 1);
    const mix = sectionPos - left;
    const envelope = smoothedSections[left] * (1 - mix) + smoothedSections[right] * mix;

    return Math.min(1, base * envelope + 0.06);
  });
}

const WaveformProgress = ({ songId, position, duration, onSeek, themeMode }) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const heightsRef = useRef([]);

  // Recompute waveform bars only when songId changes
  useEffect(() => {
    heightsRef.current = generateWaveform(songId, BAR_COUNT);
  }, [songId]);

  // Always-current draw function stored in a ref so ResizeObserver never needs to re-run
  const drawRef = useRef(null);
  drawRef.current = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const w = canvas.width;
    const h = canvas.height;
    const heights = heightsRef.current;

    ctx.clearRect(0, 0, w, h);

    const gap = 1;
    const segmentW = w / BAR_COUNT;
    const barW = Math.max(1, segmentW - gap);
    const progress = duration > 0 ? Math.min(position / duration, 1) : 0;
    const playheadX = Math.floor(progress * w);

    heights.forEach((ratio, i) => {
      const x = Math.round(i * segmentW);
      const barH = Math.max(2, ratio * h);
      const y = (h - barH) / 2;
      const isPlayed = x + barW <= playheadX;
      const drawWidth = i === BAR_COUNT - 1 ? Math.max(1, w - x) : Math.max(1, Math.round(barW));

      if (isPlayed) {
        const grad = ctx.createLinearGradient(0, y, 0, y + barH);
        grad.addColorStop(0, '#FF2200');
        grad.addColorStop(1, '#550000');
        ctx.fillStyle = grad;
      } else {
        ctx.fillStyle = themeMode === 'dark' ? '#3a3a3a' : 'rgba(0,0,0,0.22)';
      }
      ctx.fillRect(x, y, drawWidth, barH);
    });

    // Playhead cursor
    if (duration > 0) {
      ctx.fillStyle = themeMode === 'dark' ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.85)';
      ctx.fillRect(playheadX - 1, 0, 2, h);
    }
  };

  // ResizeObserver runs once — always calls the latest draw via drawRef
  useEffect(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas) return;

    let resizeRaf;
    let lastWidth = 0;
    const resize = () => {
      const nextWidth = container.clientWidth;
      if (!nextWidth || nextWidth === lastWidth) return;
      lastWidth = nextWidth;
      canvas.width = nextWidth;
      canvas.height = HEIGHT;
      drawRef.current();
    };

    const scheduleResize = () => {
      cancelAnimationFrame(resizeRaf);
      resizeRaf = requestAnimationFrame(resize);
    };

    const ro = new ResizeObserver(scheduleResize);
    ro.observe(container);
    scheduleResize();
    return () => {
      cancelAnimationFrame(resizeRaf);
      ro.disconnect();
    };
  }, []);

  // Redraw whenever position or duration changes (no canvas resize)
  useEffect(() => {
    drawRef.current?.();
  }, [position, duration]);

  const handleClick = (e) => {
    const canvas = canvasRef.current;
    if (!canvas || !duration) return;
    const rect = canvas.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    onSeek(Math.max(0, Math.min(ratio * duration, duration)));
  };

  return (
    <Box
      ref={containerRef}
      onClick={handleClick}
      onTouchEnd={(e) => {
        if (!canvasRef.current || !duration) return;
        const touch = e.changedTouches[0];
        const rect = canvasRef.current.getBoundingClientRect();
        const ratio = (touch.clientX - rect.left) / rect.width;
        onSeek(Math.max(0, Math.min(ratio * duration, duration)));
      }}
      sx={{ width: '100%', cursor: duration > 0 ? 'pointer' : 'default', borderRadius: '4px', overflow: 'hidden' }}
    >
      <canvas
        ref={canvasRef}
        style={{
          display: 'block',
          width: '100%',
          maxWidth: '100%',
          margin: 0,
          padding: 0,
          boxSizing: 'border-box',
          cursor: duration > 0 ? 'pointer' : 'default',
          touchAction: duration > 0 ? 'none' : 'auto',
        }}
      />
    </Box>
  );
};

export default WaveformProgress;
